import { useEffect, useRef, useState } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';

import { voteWindowSecs } from './voteWindow';

/**
 * Seconds left in the run-it-twice vote window, ticking locally.
 *
 * The server anchors the window to `game.lastmove` (epoch seconds) and closes it
 * when `game.time - game.lastmove` exceeds `game.multiRunVoteSecs`. Those fields
 * only advance on a BROADCAST, so they cannot be read directly to know the window
 * has shut — between the deadline and the next frame they still say there is time
 * left. So the wall-clock end is re-derived from them on every broadcast and
 * counted down locally: in sync with the server without trusting the client
 * clock's absolute offset, the same drift-tolerant approach SeatTimer uses.
 *
 * Shared by the countdown dial and the vote buttons. The buttons need it because
 * the panel stays mounted until `runs` loses its 'v' on the next broadcast, and a
 * click in that gap is refused by BOTH writers with no reply channel to say so —
 * the player sees their vote register in the UI and it was never recorded.
 * Offering only what the server will still accept is the same rule
 * `hasStakeInHand` applies to eligibility.
 */
export const useVoteCountdown = (): {
  remaining: number;
  windowSecs: number;
  hasExpired: boolean;
} => {
  const lastmove = useTableSelector((table) => table.game.lastmove);
  const time = useTableSelector((table) => table.game.time);
  const voteSecs = useTableSelector((table) => table.game.multiRunVoteSecs);

  // The table's own window, as resolved and broadcast by the server. Shared with
  // the cross-table indicator so the two cannot drift (EN-10237).
  const windowSecs = voteWindowSecs({ multiRunVoteSecs: voteSecs });

  const [remaining, setRemaining] = useState(windowSecs);
  // Wall-clock (ms) at which the window closes, re-anchored on each broadcast.
  const endAtRef = useRef<number>(0);

  useEffect(() => {
    // NaN here does not degrade, it DISABLES the expiry. A payload without
    // `time`/`lastmove` — a mid-window reconnect before the first full frame,
    // or the /demo-table preview — makes `+undefined - +undefined` NaN, which
    // survives every Math.max below, leaves `remaining` NaN, and makes
    // `hasExpired` (NaN <= 0) permanently FALSE. The vote buttons then stay
    // clickable past the deadline and every click is silently refused by both
    // writers. An unknown anchor means "the window just opened", the same
    // fail-open direction voteWindowSecs takes on an unusable window.
    const elapsed = +time - +lastmove;
    const serverElapsed = Number.isFinite(elapsed) ? Math.max(elapsed, 0) : 0;
    const secsLeft = Math.max(windowSecs - serverElapsed, 0);
    endAtRef.current = Date.now() + secsLeft * 1000;
    setRemaining(Math.ceil(secsLeft));

    const id = setInterval(() => {
      const left = Math.max((endAtRef.current - Date.now()) / 1000, 0);
      setRemaining(Math.ceil(left));
      if (left <= 0) clearInterval(id);
    }, 250);

    return () => clearInterval(id);
  }, [lastmove, time, windowSecs]);

  return { remaining, windowSecs, hasExpired: remaining <= 0 };
};
